import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ClipboardList, Trash2, Loader2, Activity, Flame, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import FREE from "@/lib/classroom_free.json";
import { Leaderboard } from "@/components/smart/Leaderboard";

const TARGETS = {
  lesson: FREE.map((l) => [l.slug, `Lesson ${l.n}: ${l.title}`]),
  lab: [["rover", "Robot Lab: Obstacle-avoiding rover"], ["linefollower", "Robot Lab: Line-follower robot"], ["arm", "Robot Lab: Robotic arm"], ["drone", "Robot Lab: Quadcopter drone"], ["racedrone", "Robot Lab: FPV racing drone"], ["deliverydrone", "Robot Lab: Delivery drone"], ["rescuedrone", "Robot Lab: AI rescue drone"], ["walker", "Robot Lab: Walking humanoid"], ["dancer", "Robot Lab: Dancing humanoid"]],
  challenge: [["today", "Daily challenge"]],
};

const AssignForm = ({ auth, onDone }) => {
  const [f, setF] = useState({ kind: "lesson", target: TARGETS.lesson[0]?.[0] || "", class_grade: "", due_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/teacher/smart/assignments", f, auth); toast.success("Module assigned"); setF({ ...f, note: "" }); onDone(); }
    catch (er) { toast.error(er.response?.data?.detail || "Could not assign"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="smart-assign-form">
      <label className="text-xs font-semibold text-slate-600 lg:col-span-1">Module type
        <Select value={f.kind} onValueChange={(k) => setF({ ...f, kind: k, target: TARGETS[k][0][0] })}>
          <SelectTrigger className="mt-1 h-10 bg-white" data-testid="smart-assign-kind"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{["lesson", "lab", "challenge"].map((k) => <SelectItem key={k} value={k} data-testid={`smart-assign-kind-${k}`}>{k[0].toUpperCase() + k.slice(1)}</SelectItem>)}</SelectContent>
        </Select>
      </label>
      <label className="text-xs font-semibold text-slate-600 sm:col-span-1 lg:col-span-2">Module
        <Select value={f.target} onValueChange={(t) => setF({ ...f, target: t })}>
          <SelectTrigger className="mt-1 h-10 bg-white" data-testid="smart-assign-target"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{TARGETS[f.kind].map(([v, t]) => <SelectItem key={v} value={v} data-testid={`smart-assign-target-${v}`}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </label>
      <label className="text-xs font-semibold text-slate-600">Class (blank = all)
        <input value={f.class_grade} onChange={(e) => setF({ ...f, class_grade: e.target.value })} maxLength={8} data-testid="smart-assign-class" className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm" placeholder="e.g. 9" />
      </label>
      <div className="text-xs font-semibold text-slate-600">Due date
        <Popover open={pick} onOpenChange={setPick}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="mt-1 h-10 w-full justify-start font-normal" data-testid="smart-assign-due">
              <CalendarDays className="w-4 h-4 mr-2 text-[#FF2E63]" />{f.due_date ? format(new Date(`${f.due_date}T00:00:00`), "dd MMM yyyy") : "No due date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid="smart-assign-due-picker">
            <Calendar mode="single" selected={f.due_date ? new Date(`${f.due_date}T00:00:00`) : undefined} disabled={{ before: new Date() }} initialFocus
              onSelect={(d) => { setF({ ...f, due_date: d ? format(d, "yyyy-MM-dd") : "" }); setPick(false); }} />
          </PopoverContent>
        </Popover>
      </div>
      <Button type="submit" disabled={busy} className="rs-cta rounded-full h-10" data-testid="smart-assign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}</Button>
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={200} data-testid="smart-assign-note" placeholder="Note for students (optional)"
        className="sm:col-span-2 lg:col-span-6 h-10 px-3 rounded-md border border-slate-300 text-sm" />
    </form>
  );
};

const Monitor = ({ items }) => (
  <div className="overflow-x-auto" data-testid="smart-monitor">
    <table className="w-full text-sm min-w-[720px]">
      <thead><tr className="text-left text-xs text-slate-500 border-b">
        {["Student", "Stage · XP", "Level", "Robotics / Drones / AI", "Streak", "Labs", "Assigned"].map((h) => <th key={h} className="py-2 pr-3 font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>
        {items.map((s, i) => (
          <tr key={s.student_code || i} className="border-b last:border-0 align-top" data-testid={`smart-monitor-row-${i}`}>
            <td className="py-2.5 pr-3"><div className="font-semibold text-[#0A192F]">{s.student_name}</div><div className="text-[11px] text-slate-500">Class {s.class_grade}{s.roll_no ? ` · Roll ${s.roll_no}` : ""}</div>
              <div className="text-[11px] text-slate-500 max-w-[260px]">{s.summary}</div></td>
            <td className="py-2.5 pr-3 font-semibold">{s.stage} · {s.xp}</td>
            <td className="py-2.5 pr-3 capitalize">{s.level}</td>
            <td className="py-2.5 pr-3">{s.tracks.robotics}% / {s.tracks.drones}% / {s.tracks.ai}%</td>
            <td className="py-2.5 pr-3"><span className="inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-[#B45309]" />{s.streak}</span></td>
            <td className="py-2.5 pr-3">{Object.entries(s.labs).filter(([, v]) => v.done).map(([k]) => k).join(", ") || "—"}</td>
            <td className="py-2.5 pr-3" data-testid={`smart-monitor-assigned-${i}`}>{s.assigned_done}/{s.assigned_total}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const TeacherSmartPanel = ({ auth }) => {
  const [list, setList] = useState(null);
  const [mon, setMon] = useState(null);
  const load = useCallback(() => {
    api.get("/teacher/smart/assignments", auth).then((r) => setList(r.data.items)).catch(() => setList([]));
    api.get("/teacher/smart/monitor", auth).then((r) => setMon(r.data.items)).catch(() => setMon([]));
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (window.location.hash === "#smart") setTimeout(() => document.getElementById("smart")?.scrollIntoView({ behavior: "smooth" }), 400); }, []);
  const remove = async (id) => {
    try { await api.delete(`/teacher/smart/assignments/${id}`, auth); toast.success("Assignment removed"); load(); } catch { toast.error("Could not remove"); }
  };
  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5" id="smart" data-testid="teacher-smart-panel">
      <h2 className="font-bold text-lg text-[#0A192F] flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#FF2E63]" />Smart classroom · assign modules</h2>
      <p className="text-xs text-slate-500 mb-4">Assigned modules appear first in each student's learning path on the Virtual Classroom.</p>
      <AssignForm auth={auth} onDone={load} />
      <ul className="mt-5 divide-y" data-testid="smart-assign-list">
        {list === null ? <li className="py-3 text-sm text-slate-500">Loading…</li> : list.length === 0 ? <li className="py-3 text-sm text-slate-500" data-testid="smart-assign-empty">No modules assigned yet.</li>
          : list.map((a) => (
            <li key={a.id} className="py-3 flex items-center gap-3" data-testid={`smart-assign-item-${a.id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#0A192F]">{a.title}</div>
                <div className="text-xs text-slate-500">{a.class_grade ? `Class ${a.class_grade}` : "All classes"}{a.due_date ? ` · due ${a.due_date}` : ""}{a.note ? ` · ${a.note}` : ""}</div></div>
              <button onClick={() => remove(a.id)} aria-label="Remove assignment" data-testid={`smart-assign-delete-${a.id}`} className="w-9 h-9 rounded-full grid place-items-center border border-slate-300 hover:border-[#FF3B3B] hover:text-[#FF3B3B]"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
      </ul>
      <h3 className="mt-6 font-bold text-[#0A192F] flex items-center gap-2"><Activity className="w-5 h-5 text-[#007A8A]" />Monitor students</h3>
      {mon === null ? <p className="mt-2 text-sm text-slate-500">Loading…</p> : mon.length === 0
        ? <p className="mt-2 text-sm text-slate-500" data-testid="smart-monitor-empty">No approved student accounts from your school yet.</p> : <Monitor items={mon} />}
      <div className="mt-6"><Leaderboard teacherAuth={auth} /></div>
    </div>
  );
};
